/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  AppRouter,
  createDialogRoute,
  DateTimeTypes, DictionaryService,
  FormState,
  FormStateBuilder, globalDictionaries, UserContext,
} from '@gh/common-shared';
import { DictionaryItemDto, DirtyType, ID } from '@gh/common-shared-data';
import {
  AutocompleteLoadMoreEvent,
  DialogInput, NotificationService, WaitingService,
} from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';
import {
  HlpRestService,
  LeadListItemDto,
  LeadRestService,
  SalesFolderRestService,
  SalesFolderShortDto, SalesFolderStatus,
} from '@gh/builder-shared-data';
import { HlpListItem } from '@gh/builder-shared';
import { isNil } from 'lodash';
import { gridCollectionComparator, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { asString, currentDate } from '@gh/core-util';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import { CloneSalesFolder, cloneSalesFolderComparator, getCloneSalesFolderId } from '../../../leads/resources/quote.model';
import { leadDetailsInitFromDto, QuoteDetailsBusinessService } from '../../../leads';

export interface HlpCreateLeadQuoteDialogConfig {
  hlp: HlpListItem;
}

@Component({
  selector:    'gh-hlp-create-lead-quote-dialog',
  template: require('./hlp-create-lead-quote-dialog.component.html'),
  styles:   [require('./hlp-create-lead-quote-dialog.component.scss')],
})
export class HlpCreateLeadQuoteDialogComponent implements OnInit, DialogInput<HlpCreateLeadQuoteDialogConfig> {
  input: HlpCreateLeadQuoteDialogConfig;
  isCreateSalesFolderMode = false;
  createSfForm: FormState<any>;
  form: FormState<any>;
  priorities: DictionaryItemDto[] = [];
  salesFolders: SalesFolderShortDto[] = [];
  lead?: any;
  salesFolderLoading = false;
  salesFolderGridDefinition: GridDefinition;
  selectedSalesFolder?: CloneSalesFolder;

  get isContactSelected(): boolean {
    return !!this.form.getControl('customer').value;
  }

  get selectedSalesFolderId(): Maybe<ID> {
    const { selectedSalesFolder } = this;

    return selectedSalesFolder ? selectedSalesFolder.salesFolderId : void 0;
  }

  constructor(
    private dialogRef: MatDialogRef<any>,
    private translationService: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private formStateBuilder: FormStateBuilder,
    private router: AppRouter,
    private leadsRest: LeadRestService,
    private hlpRest: HlpRestService,
    private userContext: UserContext,
    private salesFoldersRest: SalesFolderRestService,
    private waitingService: WaitingService,
    private notificationService: NotificationService,
    private dictionaryService: DictionaryService,
    private businessService: QuoteDetailsBusinessService,
  ) {

  }

  ngOnInit(): void {
    this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority).subscribe((res) => this.priorities = res);

    this.form = this.formStateBuilder.buildPlain({
      value:    {
        customer: '',
      },
      children: {
        customer: { required: true },
      },
    });

    this.salesFolderGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      columns: [{
        field:       'name',
        headerName:  this.translationService.get('lbl_name'),
        width:       300,
        comparator:  gridCollectionComparator(cloneSalesFolderComparator),
        valueGetter: ({ data }: { data: CloneSalesFolder }) => `${ DateTimeTypes.ShortDate.format(data.created) } - ${ asString(
          data.name) }`,
      }],
      model:   createDefaultGridModel({
        getId: getCloneSalesFolderId,
      }),
      options: { rowSelection: 'single' },
    });
  }

  loadCustomers(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findLeadsByName(event.query, {
      showActive: true,
      showCompleted: true,
      showInactive: true,
    }));
  }

  onCustomerSelected(contact: LeadListItemDto): void {
    this.salesFolders = [];

    if (!this.isContactSelected) {
      this.selectedSalesFolder = undefined;
      return;
    }
    this.salesFolderLoading = true;
    this.leadsRest.get(contact.contactId).pipe(map$(leadDetailsInitFromDto)).subscribe(
      (response) => {
        this.lead = response;
        this.updateSalesFolderList(response.salesFolders);
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCreateSfForm(): void {
    this.isCreateSalesFolderMode = true;
    this.createSfForm = this.formStateBuilder.buildPlain({
      value:    {
        name:     '',
        priority: null,
      },
      children: {
        name:     { required: true },
        priority: { required: true },
      },
    });
  }

  onCancelCreateSf(): void {
    this.isCreateSalesFolderMode = false;
    this.createSfForm.refresh();
  }

  onApplyCreateSf(): void {
    const customer = this.form.getControl('customer').value;
    if (isNil(customer)) {
      return;
    }

    this.isCreateSalesFolderMode = false;
    this.leadsRest.update({
      contactId:      customer.contactId,
      typeOfCustomer: this.lead.typeOfCustomer,
      salespersonId:  this.lead.salespersonId,
      dirtyType:      DirtyType.Updated,
      version:        this.lead.version,
      salesFolder:    {
        ...this.createSfForm.submitValue,
        priority:  Number(this.createSfForm.submitValue.priority),
        createdLocal: currentDate(),
        dirtyType:    DirtyType.New,
        stage:        0, // Stages.New
        status:       SalesFolderStatus.Active,
      },
    }).pipe(
      this.waitingService.operator())
      .subscribe((response) => {
        this.salesFolderLoading = true;
        this.notificationService.success('msg_created_successfully');
        this.lead = response;
        this.updateSalesFolderList(response.salesFolders);
      },         this.notificationService.processAll);
  }

  onSalesFolderSelected(id?: ID): void {
    if (isNil(id)) {
      return;
    }

    this.selectedSalesFolder = this.salesFolders.find((item) => item.salesFolderId === id);
  }

  onCreate(): void {
    const { selectedSalesFolder } = this;

    if (isNil(selectedSalesFolder)) {
      return;
    }
    this.hlpRest.createLeadQuote(this.input.hlp.id, selectedSalesFolder.salesFolderId)
      .subscribe((quote) => {
        this.router.navigateToChild([`/sales/sales-folders/${ quote.salesFolderId }/rfq/quotations/${ quote.quotationId }`], {
          parentTitle: 'lbl_hlps',
        });
        this.dialogRef.close();
      },         this.notificationService.processAll);

  }

  private updateSalesFolderList(salesFolders: any): void {
    this.salesFolderLoading = false;
    this.salesFolders = salesFolders;
    this.selectedSalesFolder = salesFolders[0];
    if (this.selectedSalesFolderId) {
      this.onSalesFolderSelected(this.selectedSalesFolderId);
    }
  }
}

export const hlpCreateLeadQuoteDialogRoute = createDialogRoute({
  component: HlpCreateLeadQuoteDialogComponent,
});
